// OrphanScreen — valid Telegram user, not linked to any tenant.
// Faithful port of the design bundle (screens-auth.jsx · AuthOrphan), wired to
// the real auth state. M-4: autopolls /auth every 30s (stops after 10 = 5 min)
// so a manager who opens before the admin finishes binding lands in-app
// without tapping; plus an "I have an invite" hint pointing at the deeplink.
import { useEffect, useRef, useState } from 'react'

import { useAuth } from '../auth/AuthProvider'
import { AuthTopBar } from '../components/AuthTopBar'
import { RequestAccessScreen } from './RequestAccessScreen'
import { getWebApp } from '../telegram/tg'
import { Button, Icon, t } from '../design'

const POLL_MS = 30_000
const POLL_MAX = 10

function detectedHandle(): string {
  const u = (getWebApp()?.initDataUnsafe?.user ?? {}) as {
    username?: string
    first_name?: string
    id?: number
  }
  if (u.username) return `@${u.username}`
  if (u.first_name) return u.first_name
  if (u.id) return `id ${u.id}`
  return '—'
}

export function OrphanScreen() {
  const { retry } = useAuth()
  const handle = detectedHandle()
  const [polling, setPolling] = useState(true)
  const [view, setView] = useState<'orphan' | 'request'>(
    // dev preview: ?orphan_view=request opens the form directly
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('orphan_view') === 'request'
      ? 'request' : 'orphan',
  )
  const pollsRef = useRef(0)

  // Background recheck loop. `retry` flips auth → 'authing' then back to
  // 'orphan' if still unbound; this component remounts each cycle, so the
  // poll count lives in a ref seeded from sessionStorage to survive remounts.
  useEffect(() => {
    pollsRef.current = Number(sessionStorage.getItem('pt:orphan_polls') || '0')
    if (pollsRef.current >= POLL_MAX) { setPolling(false); return }
    const id = setInterval(() => {
      pollsRef.current += 1
      sessionStorage.setItem('pt:orphan_polls', String(pollsRef.current))
      if (pollsRef.current >= POLL_MAX) {
        clearInterval(id)
        setPolling(false)
      }
      retry()
    }, POLL_MS)
    return () => clearInterval(id)
  }, [retry])

  // Autopoll keeps running underneath the request form (hooks above always
  // fire) — so if a director approves while the user is typing, auth flips to
  // 'ok' and this whole orphan tree unmounts into the app.
  if (view === 'request') return <RequestAccessScreen onBack={() => setView('orphan')} />

  const close = () => {
    try { getWebApp()?.close() } catch { /* noop outside Telegram */ }
  }
  const manualRecheck = () => {
    sessionStorage.setItem('pt:orphan_polls', '0')
    pollsRef.current = 0
    setPolling(true)
    retry()
  }

  return (
    <>
      <AuthTopBar onBack={close} />
      <div className="scroll-y" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 18, padding: '20px 16px' }}>
        <div style={{ textAlign: 'center' }}>
          <span style={{ width: 72, height: 72, borderRadius: 20, background: 'var(--warning-bg)', color: 'var(--warning)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
            <Icon.Users size={32} sw={2} />
          </span>
          <div className="t-h1" style={{ marginBottom: 6 }}>{t('auth.orphan.title')}</div>
          <div className="t-caption" style={{ maxWidth: 290, margin: '0 auto', lineHeight: 1.5 }}>{t('auth.orphan.body')}</div>
        </div>

        <div className="card card-16" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: 'var(--surface-2)', color: 'var(--ai)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon.Telegram size={18} sw={2} />
          </span>
          <div className="flex-1" style={{ minWidth: 0 }}>
            <div className="t-caption">{t('auth.orphan.detected')}</div>
            <div className="t-body-s t-mono" style={{ marginTop: 1 }}>{handle}</div>
          </div>
        </div>

        <div className="col" style={{ gap: 8 }}>
          <Button variant="primary" full icon={<Icon.Refresh size={16} sw={2} />} onClick={manualRecheck}>
            {t('inv.orphan.recheck')}
          </Button>
          {polling && (
            <div className="row middle gap-2" style={{ justifyContent: 'center', marginTop: 2 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--success)', animation: 'pulse 1.4s ease-in-out infinite' }} />
              <span className="t-caption" style={{ color: 'var(--text-3)' }}>{t('inv.orphan.autopoll')}</span>
            </div>
          )}
        </div>

        {/* "I have an invite" hint */}
        <div className="card card-16" style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
          <span style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: 'var(--brand-100)', color: 'var(--brand-900)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon.Link size={18} sw={2} />
          </span>
          <div className="flex-1" style={{ minWidth: 0 }}>
            <div className="t-body-s">{t('inv.orphan.haveInvite')}</div>
            <div className="t-caption">{t('inv.orphan.haveInviteHint')}</div>
          </div>
        </div>

        {/* Self-service request access (M-7 §3.6) */}
        <button onClick={() => setView('request')} className="card card-16"
                style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', border: 'none', background: 'var(--surface)', cursor: 'pointer', textAlign: 'left' }}>
          <span style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: 'var(--ai-bg)', color: 'var(--ai)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon.Send size={18} sw={2} />
          </span>
          <div className="flex-1" style={{ minWidth: 0 }}>
            <div className="t-body-s">{t('req.entry')}</div>
            <div className="t-caption">{t('req.entryHint')}</div>
          </div>
          <Icon.ArrowRight size={18} sw={2} style={{ color: 'var(--text-3)' }} />
        </button>
      </div>
    </>
  )
}
